import { StoreModelCollection } from "@bitwarden/web-vault/app/services/store/model.collection.abstraction";
import { StoreModelNames } from "../../dali/type/dali.type";
import { BudgetStoreModel } from "../../../models/store/budget.store.model";
import { signal } from "@angular/core";
import { BudgetView } from "@bitwarden/web-vault/app/models/view/budget/budget.view";
import { repairDanglingReferences } from "@bitwarden/web-vault/app/services/store/referential-repair";

export class BudgetStoreCollection extends StoreModelCollection {
  readonly type: StoreModelNames = "BudgetStoreModel";

  protected models: Map<string, BudgetStoreModel> = new Map<string, BudgetStoreModel>();

  private _budgetViews = signal<BudgetView[]>(null);
  readonly budgetViews = this._budgetViews.asReadonly();

  /**
   * Strip the given (deleted) category ids from every budget's `ctId`, persisting only the budgets
   * that actually referenced one. Registered as a referential-dependent hook on the categories
   * collection (see BudgetStoreService's constructor), so deleting a category can never leave a budget
   * holding its id — the cross-collection counterpart of the transaction `lTid` repair (#4442/#5565).
   * Reuses the shared `repairDanglingReferences` scan; the write mirrors the transaction repair
   * (persist the fresh copies, re-hydrate from the echoed models, then refresh the view signal).
   */
  async stripDanglingCategoryRefs(deletedCategoryIds: ReadonlySet<string>): Promise<void> {
    const repaired = repairDanglingReferences(this.models.values(), "ctId", deletedCategoryIds);
    if (!repaired.length) {
      return;
    }
    try {
      const saved = await this.daliService.save(this.type, repaired);
      // Re-hydrate through loadModels (validate + deepFreeze), exactly as the base save()/saveToVault()
      // do — not a raw models.set of the echo. Every other insertion path validates the echoed model
      // first; merging the save result unchecked here would let a missing/mismatched-field echo be
      // stored raw and later crash an unrelated triggerChanges() via `new BudgetView(model)`. This is
      // the budget counterpart of the transaction repair's echo validation (#4699/#4700).
      this.loadModels(saved);
      this.triggerChanges();
    } catch (error) {
      // Mirror the base save()/saveToVault() paths: a partial vault write persisted some budgets
      // with their new vid before failing. Load that committed subset back into memory (via the
      // shared helper) so the aborted-and-retried category delete re-strips against the fresh vids
      // instead of writing duplicate ciphers against a stale one (#4908), then rethrow so the caller
      // (the delete hook) still aborts.
      if (this.loadCommittedOnPartialWrite(error)) {
        this.triggerChanges();
      }
      throw error;
    }
  }

  triggerChanges(): void {
    this._budgetViews.set(this.buildViews(this.models, (model) => new BudgetView(model)));
  }

  clear(): void {
    this.models.clear();
    this._budgetViews.set(null);
  }
}
